"use client";
import Image from "next/image";
import React from "react";
import { TransitionLink } from "../shared/TransitionLink";
import { FiArrowRight } from "react-icons/fi";
import { BlogProps } from "@/types/blog/blog.type";
import Link from "next/link";
import { useDict, useLocale } from "@/components/providers/LocaleProvider";
import { pickBlogLocale } from "@/libs/blog.libs";

interface BlogCardProps {
  blog: BlogProps;
}

interface BlogListProps {
  blogs: BlogProps[];
}

const BlogList = ({ blogs }: BlogListProps) => {
  return (
    <section className="space-y-12 md:space-y-14">
      {blogs.reverse().map((blog) => (
        <BlogCard key={blog.id} blog={blog} />
      ))}
    </section>
  );
};

function BlogCard({ blog }: BlogCardProps) {
  const t = useDict();
  const { locale } = useLocale();
  const copy = pickBlogLocale(blog, locale);
  return (
    <section className="grid md:grid-cols-2 gap-4">
      <div className=" relative w-full max-md:h-[250px] md:h-[280px] lg:h-[230px]">
        <Image
          src={blog.thumbnail}
          fill
          alt={t.blogsList.blogImageAlt}
          className=" rounded-xl object-cover max-md:w-full"
        />
      </div>

      <div className=" space-y-6">
        <article className=" space-y-2">
          <p className=" text-xl md:text-2xl font-medium">{copy.title}</p>
          <p className=" text-sm md:text-base">{copy.shortDescription}</p>
        </article>
        <div className="w-full flex justify-between items-center">
          <TransitionLink
            href={`/blogs/${blog.endpoint}`}
            className="flex items-center group  text-sm md:text-base gap-x-1 cursor-pointer text-text-primary-ligt font-semibold"
          >
            {t.blogsList.readMore}{" "}
            <FiArrowRight className=" group-hover:ml-1 duration-200" />
          </TransitionLink>

          <div className="flex justify-end items-center gap-x-2">
            {blog.links.linkedin && (
              <Link href={blog.links.linkedin} target="_blank">
                <Image
                  src="/assets/social-icons/linkedin.svg"
                  width={28}
                  height={28}
                  alt={t.blogsList.linkedinLogoAlt}
                  className=" cursor-pointer"
                />
              </Link>
            )}
            {blog.links.medium && (
              <Link href={blog.links.medium} target="_blank">
                <Image
                  src="/assets/third-party-icons/medium-icon-black.avif"
                  width={24}
                  height={24}
                  alt={t.blogsList.mediumLogoAlt}
                  className=" cursor-pointer"
                />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default BlogList;
